import React from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { TimePeriod } from '@/hooks/useReportFilters';

interface TimePeriodSelectionProps {
    value: TimePeriod;
    startDate: Date | null;
    endDate: Date | null;
    onChange: (value: TimePeriod) => void;
    onDateChange: (start: Date | null, end: Date | null) => void;
}

const options: { label: string; value: TimePeriod }[] = [
    { label: '1 Day', value: '1 Day' },
    { label: '7 Days', value: '7 Days' },
    { label: '30 Days', value: '30 Days' },
    { label: '3 Months', value: '3 Months' },
    { label: 'Custom', value: 'Custom' },
];

export const TimePeriodSelection: React.FC<TimePeriodSelectionProps> = ({
    value,
    startDate,
    endDate,
    onChange,
    onDateChange,
}) => {
    return (
        <div className="space-y-4">
            <Label className="text-sm font-medium">Time Period</Label>
            <RadioGroup
                value={value}
                onValueChange={(val) => onChange(val as TimePeriod)}
                className="flex flex-wrap gap-4"
            >
                {options.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.value} id={`period-${option.value}`} />
                        <Label htmlFor={`period-${option.value}`} className="cursor-pointer">
                            {option.label}
                        </Label>
                    </div>
                ))}
            </RadioGroup>

            {value === 'Custom' && (
                <div className="flex flex-col sm:flex-row gap-3 mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex-1 space-y-1.5">
                        <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider ml-1">Start Date</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn(
                                        "w-full justify-start text-left font-normal h-9 px-3",
                                        !startDate && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                    <span className="truncate">
                                        {startDate ? format(startDate, "PP") : "Pick a date"}
                                    </span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={startDate || undefined}
                                    onSelect={(date) => onDateChange(date || null, endDate)}
                                    disabled={(date) => date > new Date()}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="flex-1 space-y-1.5">
                        <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider ml-1">End Date</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn(
                                        "w-full justify-start text-left font-normal h-9 px-3",
                                        !endDate && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                    <span className="truncate">
                                        {endDate ? format(endDate, "PP") : "Pick a date"}
                                    </span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={endDate || undefined}
                                    onSelect={(date) => onDateChange(startDate, date || null)}
                                    disabled={(date) => date > new Date() || (startDate ? date < startDate : false)}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            )}
        </div>
    );
};
